/* eslint-disable @typescript-eslint/no-unused-vars */
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { selectIsPagination } from 'Selector/GlobalSearchResultsSelector';
import SearchIndustrySkeletonBlock from 'Components/common/skeleton/SearchIndustrySkeletonBlock';
import IndustriesWidget from 'Components/common/components/IndustriesWidget';
import IndustriesSkeleton from 'Components/common/skeleton/IndustriesSkeleton';
import { actions } from 'Slice/IndustriesSlice';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { actions as GlobalSearchActions } from 'Slice/GlobalSearchResultsSlice';
import { Industry } from 'Types/IndustryTypes';
import { SlicerResultPage, valueToObject } from 'Utils/UtilityFunctions';
import { FEED_GILROY_ROUTE, SEARCH, SEARCH_ROUTE } from 'Utils/Constants';
import classNames from 'classnames';
import { SearchResultsRouteState } from 'Pages/search/GlobalSearchResults';
import HeadingWithShowAll from './HeadingWithShowAll';
import SectionHeading from './SectionHeading';

type Props = {
  heading: string,
  industriesCollection?: Industry[]
  loading?: boolean,
  industryCount?: Industry[],
  hideDefaultBackground?: boolean,
  showLoaderForLessResults?: boolean,
  numberOfLoaderForLessResults?: number,
  count?: number,
  showUnfollow?: boolean,
  setIndustryTop?: (a: string) => void
  query?: string,
  inGlobalSearch?: boolean,
} & typeof defaultProps;

const defaultProps = {
  loading: false,
  industriesCollection: [] as Industry[],
  industryCount: [] as Industry[],
  hideDefaultBackground: false,
  showLoaderForLessResults: false,
  numberOfLoaderForLessResults: 0,
  count: 0,
  showUnfollow: false,
  setIndustryTop(a: string) { },
  query: '',
  inGlobalSearch: false,
};

const IndustriesSectionGlobalSearch = function IndustriesSectionGlobalSearch(props: Props) {
  const [searchParams] = useSearchParams();
  const extendedSections = ['person', 'company', 'article', 'industry'];
  const extendedSection = (searchParams.get('extend') ?? '');
  const isDetailedSearch: boolean = extendedSections.includes(extendedSection);
  const dispatch = useDispatch();
  const isPagination: boolean = useSelector(selectIsPagination);
  const slicerNumber: number = SlicerResultPage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    heading, loading,
    industriesCollection, count, industryCount, hideDefaultBackground,
    showLoaderForLessResults,
    showUnfollow, setIndustryTop,
    numberOfLoaderForLessResults,
    query, inGlobalSearch,
  } = props;

  const location = useLocation();
  const { state } = location as { state: SearchResultsRouteState };
  const previousPath = (state)?.previousPath ?? FEED_GILROY_ROUTE;

  const createIndustriesRoute = () => {
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('searchPage_industries_see_all_clicked', 0),
    ));
    window.scrollTo(0, 0);
    setIndustryTop('industry');
    return navigate(`${pathname}?query=${query}&extend=industry`, { state: { enableBackButton: true, previousPath } });
  };

  const loadersArray = [];
  if (showLoaderForLessResults) {
    for (let i = 0; i < numberOfLoaderForLessResults; i += 1) {
      loadersArray.push(<IndustriesSkeleton />);
    }
  }
  const followIndustry = (industry: Industry) => {
    dispatch(actions.addingIndustry(industry));
    dispatch(GlobalSearchActions.searchIndustryFollowLoading(industry.id));
  };

  const unFollowingIndustry = (industry: Industry[]) => {
    dispatch(actions.removingIndustries(industry));
  };

  const navigateToSearchResult = () => {
    const path = `${SEARCH_ROUTE}${query}`;
    navigate(path, {
      state: isDetailedSearch ? { enableBackButton: true, previousPath } : { previousPath },
    });
  };

  return (
    loading
      ? <SearchIndustrySkeletonBlock sliceFlag={!isDetailedSearch} />
      : (
        <>
          <div className={` ${hideDefaultBackground ? '' : 'fancy-box'}`}>
            <HeadingWithShowAll
              inGlobalSearch={inGlobalSearch}
              isShowAllHidden={isDetailedSearch ? true
                : industryCount && industryCount.length <= slicerNumber}
              leftHeading={(
                <SectionHeading>{`${heading} (${count})`}</SectionHeading>)}
              onClick={createIndustriesRoute}
              onSeelessClick={navigateToSearchResult}
              isDetailedSearch={isDetailedSearch}
            />
            <div>
              <Row
                className={classNames('Grid-width-row col-six-fix-md', {
                  gridUpdated: inGlobalSearch,
                })}
                gutter={[{
                  xs: 16, lg: 40, md: 32, sm: 32,
                }, 8]}
              >
                {industriesCollection?.map((industry) => (
                  <Col span={4} xxl={{ span: 4 }} xl={{ span: 4 }} lg={{ span: 6 }} className="gutter-row">
                    <IndustriesWidget
                      articleBanner={industry.image}
                      articleText={industry.name}
                      isAdded={industry.added}
                      followLoading={industry.loading}
                      id={industry.id}
                      followIndustry={() => followIndustry(industry)}
                      showUnfollow={showUnfollow}
                      unFollowingIndustry={() => unFollowingIndustry([industry])}
                      isSearchPage={pathname.includes(SEARCH)}
                      inGlobalSearch={inGlobalSearch}
                    />
                  </Col>
                ))}
                {loadersArray}
              </Row>
            </div>
          </div>
          {isDetailedSearch && isPagination && <SearchIndustrySkeletonBlock />}

        </>
      ));
};

IndustriesSectionGlobalSearch.defaultProps = defaultProps;
export default IndustriesSectionGlobalSearch;
